import purify from 'dompurify';

export function stripHtml(htmlString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.body.textContent?.replace(/\u00A0|&nbsp;/g, ' ') || '';
}

export function isRichTextEmpty(value: string): boolean {
  return stripHtml(value).trim().length === 0;
}

// these lists must match what quill allows src/constants/quillModules.ts
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'span', 'ol', 'ul', 'li'];
const ALLOWED_ATTR = ['style', 'class'];

// ALLOWED_ATTR only controls whether a `style` attribute is kept, not what CSS
// is inside it. Quill's color/background toolbar buttons only ever emit
// `color`/`background-color` declarations, so allowlist exactly those two
// properties (with a color-shaped value) rather than trying to blacklist
// dangerous patterns like `javascript:`/`expression()`, which are easy to
// obfuscate.
const ALLOWED_STYLE_PROPERTIES = new Set(['color', 'background-color']);
const SAFE_STYLE_VALUE =
  /^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d.,\s%]+\)|hsla?\([\d.,\s%]+\))$/i;

let styleHookRegistered = false;

function ensureStyleHookRegistered(): void {
  if (styleHookRegistered) {
    return;
  }
  styleHookRegistered = true;

  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') {
      return;
    }

    const declarations = data.attrValue
      .split(';')
      .map((declaration) => declaration.trim())
      .filter((declaration) => {
        const [property, value] = declaration
          .split(':')
          .map((part) => part.trim());

        return (
          !!property &&
          !!value &&
          ALLOWED_STYLE_PROPERTIES.has(property.toLowerCase()) &&
          SAFE_STYLE_VALUE.test(value)
        );
      });

    // Match the browser's own CSSOM serialization (a trailing `;` after every
    // declaration, including the last). Quill reads style attributes back via
    // `element.style.cssText`, which always has this trailing semicolon; if
    // we omit it here, our sanitized output can never equal what Quill
    // reports back to us, and react-quill-new's controlled-value diffing
    // loops forever trying to reconcile the two (see the color/background
    // toolbar bug this hook was written to guard against).
    data.attrValue = declarations.length ? declarations.join('; ') + ';' : '';
  });
}

// Matches text content that sits between/outside tags, without touching the
// tags themselves (so attribute-value quotes are never touched).
const TAG_OR_TEXT = /<[^>]*>|[^<]+/g;

// Quill's own serializer (getSemanticHTML, used to build the onChange
// payload) entity-encodes `"`/`'` in text content, borrowing lodash's
// attribute-safe `_.escape` entity map. DOMPurify's HTML serializer does not,
// since escaping quotes in text content isn't necessary for valid HTML. Left
// alone, that means our sanitized output can never equal what Quill reports
// back to us for any text containing a quote character, and react-quill-new's
// controlled-value diffing loops forever resetting the editor (the same
// failure mode the trailing-semicolon fix above guards against for `style`).
function reEscapeQuotesInText(html: string): string {
  return (html.match(TAG_OR_TEXT) || [])
    .map((segment) =>
      segment.startsWith('<')
        ? segment
        : segment.replace(/"/g, '&quot;').replace(/'/g, '&#39;'),
    )
    .join('');
}

export function sanitizeHtml(htmlString: string): string {
  ensureStyleHookRegistered();

  const sanitized = purify.sanitize(htmlString, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });

  return reEscapeQuotesInText(sanitized);
}
